import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '../ui-pages.css';
import '../shopify-app.css';
import { SkelTable } from '../components/Skeletons';

const API = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000') + '/api/integrations/shopify/public';
const STORE_APP = (process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000') + '/api/store-app';
// Public App Store listing (set REACT_APP_SHOPIFY_APP_STORE_URL once the listing is live).
const APP_STORE_URL = process.env.REACT_APP_SHOPIFY_APP_STORE_URL || 'https://apps.shopify.com/search?q=vetobase';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } });
const money = (n, c) => `${c || ''} ${Number(n || 0).toLocaleString()}`.trim();
const when = (d) => d ? new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const numId = (gid) => String(gid || '').split('/').pop();

export default function ShopifyApp() {
  const { workspaceUuid } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkCode, setLinkCode] = useState(null);
  const [linkErr, setLinkErr] = useState('');
  const [tab, setTab] = useState('products');

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/status?workspaceUuid=${workspaceUuid}`, auth()); setStatus(r.data); }
    catch { setStatus({ connected: false }); } finally { setLoading(false); }
  }, [workspaceUuid]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Installs must start from Shopify (App Store rules). An existing workspace gets a
  // short code that the merchant pastes inside the Vetobase app in Shopify admin.
  const getLinkCode = async () => {
    setLinkErr('');
    try { const r = await axios.post(`${STORE_APP}/link-code`, {}, auth()); setLinkCode(r.data); }
    catch (e) { setLinkErr(e.response?.data?.error || 'Could not create a code.'); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect this Shopify store?')) return;
    await axios.post(`${API}/disconnect`, { workspaceUuid }, auth()); loadStatus();
  };

  if (loading) return <div className="ui-page"><SkelTable rows={8} cols={5} /></div>;

  return (
    <div className="ui-page">
      <header className="ui-head">
        <div>
          <h1 className="ui-title">Shopify App</h1>
          <p className="ui-sub">Connect your store through the Vetobase Shopify app to manage products, inventory, orders & checkouts.</p>
        </div>
        {status?.connected && <button className="sh-btn ghost" onClick={disconnect}>Disconnect</button>}
      </header>

      {!status?.connected ? (
        <div className="sh-connect-card">
          <div className="sh-logo"><svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><path d="M3 6h18" /><path d="M16 10a4 4 0 0 1-8 0" /></svg></div>
          <h2>Connect your Shopify store</h2>
          <p>1. Install <b>Vetobase AI</b> from the Shopify App Store. 2. Click <b>Get link code</b> below. 3. Paste the code in the Vetobase app inside your Shopify admin.</p>
          <div className="sh-connect-row">
            <a className="sh-btn primary" href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">Open Shopify App Store</a>
            <button className="sh-btn ghost" onClick={getLinkCode}>Get link code</button>
          </div>
          {linkCode && <p style={{ marginTop: 12 }}>Your code: <b style={{ fontFamily: 'monospace', fontSize: 18, letterSpacing: 2 }}>{linkCode.code}</b> <span className="sh-muted">(valid {linkCode.expiresInMinutes} min)</span></p>}
          {linkErr && <p style={{ marginTop: 12, color: '#B23A3A' }}>{linkErr}</p>}
        </div>
      ) : (
        <>
          <div className="sh-status">
            <span className="sh-dot" />
            <b>{status.shopName || status.shopDomain}</b>
            <span className="sh-muted">· {status.shopDomain} · connected {when(status.installedAt)}</span>
            {status.scope && <span className="sh-scope">{status.scope.split(',').length} scopes</span>}
          </div>

          <div className="sh-tabs">
            {['products', 'inventory', 'orders', 'checkouts'].map(t => (
              <button key={t} className={`sh-tab${tab === t ? ' on' : ''}`} onClick={() => setTab(t)}>{t}</button>
            ))}
          </div>

          {tab === 'products' && <Products ws={workspaceUuid} />}
          {tab === 'inventory' && <Inventory ws={workspaceUuid} />}
          {tab === 'orders' && <Orders ws={workspaceUuid} />}
          {tab === 'checkouts' && <Checkouts ws={workspaceUuid} />}
        </>
      )}
    </div>
  );
}

// ------- generic paged loader hook -------
function usePaged(path, ws, key = 'items') {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const load = useCallback(async (cur) => {
    setLoading(true); setErr('');
    try {
      const r = await axios.get(`${API}${path}?workspaceUuid=${ws}${cur ? `&cursor=${encodeURIComponent(cur)}` : ''}`, auth());
      setItems(prev => cur ? [...prev, ...(r.data[key] || r.data.items || [])] : (r.data[key] || r.data.items || []));
      setCursor(r.data.cursor || null); setHasNext(!!r.data.hasNext);
    } catch (e) { setErr(e.response?.data?.error || 'Failed to load from Shopify.'); }
    finally { setLoading(false); }
  }, [path, ws, key]);
  useEffect(() => { load(null); }, [load]);
  return { items, hasNext, cursor, loading, err, load, setItems };
}

function More({ hasNext, loading, onMore }) {
  if (!hasNext) return null;
  return <div className="sh-more"><button className="sh-btn ghost" disabled={loading} onClick={onMore}>{loading ? 'Loading…' : 'Load more'}</button></div>;
}
const Empty = ({ children }) => <div className="ui-empty"><p style={{ color: '#6B6862' }}>{children}</p></div>;
const Err = ({ children }) => <div className="sh-err">{children}</div>;
const Loading = () => <SkelTable rows={8} cols={5} />;

function Products({ ws }) {
  const { items, hasNext, cursor, loading, err, load, setItems } = usePaged('/products', ws, 'items');
  const del = async (p) => {
    if (!window.confirm(`Delete product "${p.title}"?`)) return;
    try { await axios.delete(`${API}/products/${numId(p.id)}?workspaceUuid=${ws}`, auth()); setItems(prev => prev.filter(x => x.id !== p.id)); }
    catch (e) { alert('Delete failed: ' + (e.response?.data?.error || e.message)); }
  };
  if (loading && !items.length) return <Loading />;
  if (err) return <Err>{err}</Err>;
  if (!items.length) return <Empty>No products found in this store.</Empty>;
  return (
    <div className="sh-card">
      <div className="sh-table-wrap"><table className="sh-table">
        <thead><tr><th></th><th>Product</th><th>Status</th><th>Inventory</th><th>Variants</th><th /></tr></thead>
        <tbody>{items.map(p => (
          <tr key={p.id}>
            <td>{p.featuredImage?.url ? <img className="sh-thumb" src={p.featuredImage.url} alt="" /> : <div className="sh-thumb ph"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" /></svg></div>}</td>
            <td className="sh-name">{p.title}</td>
            <td><span className={`sh-badge ${String(p.status).toLowerCase()}`}>{p.status}</span></td>
            <td>{p.totalInventory ?? '—'}</td>
            <td className="sh-muted">{p.variants?.nodes?.length || 0}</td>
            <td><button className="sh-btn danger sm" onClick={() => del(p)}>Delete</button></td>
          </tr>))}</tbody>
      </table></div>
      <More hasNext={hasNext} loading={loading} onMore={() => load(cursor)} />
    </div>
  );
}

function Inventory({ ws }) {
  const [locations, setLocations] = useState([]);
  const { items, hasNext, cursor, loading, err, load } = usePaged('/products', ws, 'items');
  const [saving, setSaving] = useState('');
  useEffect(() => { axios.get(`${API}/locations?workspaceUuid=${ws}`, auth()).then(r => setLocations(r.data.locations || [])).catch(() => {}); }, [ws]);
  const setQty = async (variant, value) => {
    const loc = locations[0]; if (!loc) return alert('No Shopify location found.');
    setSaving(variant.id);
    try {
      await axios.post(`${API}/inventory/set`, { workspaceUuid: ws, inventoryItemId: numId(variant.inventoryItem?.id), locationId: numId(loc.id), available: Number(value) }, auth());
    } catch (e) { alert('Update failed: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(''); }
  };
  if (loading && !items.length) return <Loading />;
  if (err) return <Err>{err}</Err>;
  const rows = items.flatMap(p => (p.variants?.nodes || []).map(v => ({ ...v, product: p.title })));
  if (!rows.length) return <Empty>No variants found.</Empty>;
  return (
    <div className="sh-card">
      <div className="sh-muted" style={{ marginBottom: 12 }}>Location: <b>{locations[0]?.name || '—'}</b> · edit "Available" and press Enter to update.</div>
      <div className="sh-table-wrap"><table className="sh-table">
        <thead><tr><th>Product</th><th>Variant</th><th>SKU</th><th>Available</th></tr></thead>
        <tbody>{rows.map(v => (
          <tr key={v.id}>
            <td className="sh-name">{v.product}</td><td>{v.title}</td><td className="sh-muted">{v.sku || '—'}</td>
            <td><input className="sh-qty" type="number" defaultValue={v.inventoryQuantity ?? 0} disabled={saving === v.id}
              onKeyDown={e => { if (e.key === 'Enter') setQty(v, e.target.value); }} /> {saving === v.id && <span className="sh-muted">saving…</span>}</td>
          </tr>))}</tbody>
      </table></div>
      <More hasNext={hasNext} loading={loading} onMore={() => load(cursor)} />
    </div>
  );
}

function Orders({ ws }) {
  const { items, hasNext, cursor, loading, err, load } = usePaged('/orders', ws, 'items');
  if (loading && !items.length) return <Loading />;
  if (err) return <Err>{err}</Err>;
  if (!items.length) return <Empty>No orders yet.</Empty>;
  return (
    <div className="sh-card">
      <div className="sh-table-wrap"><table className="sh-table">
        <thead><tr><th>Order</th><th>Date</th><th>Customer</th><th className="sh-right">Total</th><th>Payment</th><th>Fulfillment</th></tr></thead>
        <tbody>{items.map(o => (
          <tr key={o.id}>
            <td className="sh-name">{o.name}</td><td className="sh-muted">{when(o.createdAt)}</td>
            <td>{o.customer?.displayName || o.email || '—'}</td>
            <td className="sh-right">{money(o.totalPriceSet?.shopMoney?.amount, o.totalPriceSet?.shopMoney?.currencyCode)}</td>
            <td><span className="sh-badge">{o.displayFinancialStatus || '—'}</span></td>
            <td><span className="sh-badge">{o.displayFulfillmentStatus || '—'}</span></td>
          </tr>))}</tbody>
      </table></div>
      <More hasNext={hasNext} loading={loading} onMore={() => load(cursor)} />
    </div>
  );
}

function Checkouts({ ws }) {
  const { items, hasNext, cursor, loading, err, load } = usePaged('/checkouts', ws, 'items');
  if (loading && !items.length) return <Loading />;
  if (err) return <Err>{err}</Err>;
  if (!items.length) return <Empty>No abandoned checkouts.</Empty>;
  return (
    <div className="sh-card">
      <div className="sh-table-wrap"><table className="sh-table">
        <thead><tr><th>Date</th><th>Customer</th><th className="sh-right">Total</th><th>Recover</th></tr></thead>
        <tbody>{items.map(c => (
          <tr key={c.id}>
            <td className="sh-muted">{when(c.createdAt)}</td><td>{c.customer?.displayName || '—'}</td>
            <td className="sh-right">{money(c.totalPriceSet?.shopMoney?.amount, c.totalPriceSet?.shopMoney?.currencyCode)}</td>
            <td>{c.abandonedCheckoutUrl ? <a className="sh-link" href={c.abandonedCheckoutUrl} target="_blank" rel="noreferrer">Open ↗</a> : '—'}</td>
          </tr>))}</tbody>
      </table></div>
      <More hasNext={hasNext} loading={loading} onMore={() => load(cursor)} />
    </div>
  );
}
